/* eslint-disable */
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next'

import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  FormControlLabel,
  Checkbox,
  Chip,
  Typography,
} from '@mui/material';

// constants
import { MAX_ALLOWED_GROUPS } from './groupsTable.constants';

// components
import LoadingSpinner from '../../../../../loadingSpinner'

// helpers
import {
  isGroupSelected,
  shouldDisableGroup,
  shouldDisableSelectAll,
  areAllGroupsChecked,
  createGroup,
  getGroupIds,
  getSelectedGroupsWithOutSelectedGroupId,
  sortGroups,
} from './groupsTable.helpers';
import { isSuperAdmin } from '../../../../../../helpers/user';
import { useIsMobileView } from '../../../../../../utils/isMobileView';

const GroupsTable = ({
  groups,
  selectedGroups,
  setSelectedGroups,
  areGroupsLoading,
  getSelectedGroupsCount,
  displayAlert,
}) => {
  const isSuperAdminUser = isSuperAdmin();
  const [sortBy, setSortBy] = useState('displayName');
  const [sortOrder, setSortOrder] = useState('asc');
  const { t } = useTranslation();
  const isMobileView = useIsMobileView();

  const handleCheckboxChange = (group) => {
    let updatedSelectedGroups = [];
    const selectedGroupId = group?.id;
    if (isGroupSelected(selectedGroupId, selectedGroups)) {
      updatedSelectedGroups = getSelectedGroupsWithOutSelectedGroupId(selectedGroups, selectedGroupId)
    } else {
      updatedSelectedGroups = [...selectedGroups, createGroup(group)];
    }
    const selectedGroupsCount = getSelectedGroupsCount(updatedSelectedGroups)
    if (selectedGroupsCount >= MAX_ALLOWED_GROUPS) {
      displayAlert(true)
    } else {
      displayAlert(false)
    }
    setSelectedGroups(updatedSelectedGroups);
  };

  const handleSelectAllCheckBoxChange = () => {
    const allGroupsChecked = areAllGroupsChecked(groups, selectedGroups);
    let updatedSelectedGroups = [];
    const groupIds = getGroupIds(groups);
    if (allGroupsChecked) {
      updatedSelectedGroups = selectedGroups.filter(
        (selectedGroup) => !groupIds.includes(selectedGroup?.value)
      );
    } else {
      updatedSelectedGroups = [...selectedGroups];
      groups.forEach((group) => {
        if (!isGroupSelected(group?.id, selectedGroups)) {
          updatedSelectedGroups.push(createGroup(group));
        }
      });
    }
    const selectedGroupsCount = getSelectedGroupsCount(updatedSelectedGroups)
    if (selectedGroupsCount >= MAX_ALLOWED_GROUPS) {
      displayAlert(true)
    } else {
      displayAlert(false)
    }
    setSelectedGroups(updatedSelectedGroups);
  };

  const handleSort = (property) => {
    const newOrder = sortBy === property && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(property);
    setSortOrder(newOrder);
  };

  const allGroupsChecked = areAllGroupsChecked(groups, selectedGroups);

  const sortedGroups = useMemo(() => sortGroups({ groups, sortOrder, sortBy }), [groups, sortOrder, sortBy])

  const renderTable = () => (
    <Table stickyHeader
      sx={{
        width: '100%',
        maxWidth: isMobileView ? '100%' : '97.955%',
        tableLayout: 'fixed',
        '& th, & td': {
          minWidth: 0,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word'
        },
        '& .MuiTableSortLabel-root, & .MuiButtonBase-root': {
          minWidth: 0
        },
        '& .MuiChip-root': {
          height: 'auto',
          margin: '12px 12px 12px 0',
        },
        '& .MuiChip-label': {
          overflow: 'visible',
          whiteSpace: 'normal',
          height: 'fit-content'
        },
        marginLeft: isMobileView ? '0' : '12px',
        marginRight: '0',
        marginBottom: '11px',
      }}
    >
      <TableHead>
        <TableRow sx={{
          zIndex: 1000, '& .MuiTableCell-root': {
            height: '42px',
            padding: '0',
            backgroundColor: '#f9f8f7'
          },
        }}>
          <TableCell sx={{ fontFamily: 'Johnson Display', color: '#00000', fontWeight: 'bold', fontSize: '14px', borderRight: '1px solid #C1BBB3', borderBottom: isMobileView ? 'unset' : '1px solid #C1BBB3', paddingLeft: '10px !important', width: isMobileView ? '2%' : '16%' }}>
            <FormControlLabel
              control={(
                <Checkbox
                  disabled={shouldDisableSelectAll({ groups, isSuperAdminUser, getSelectedGroupsCount, selectedGroups })}
                  sx={{
                    color: shouldDisableSelectAll({ groups, isSuperAdminUser, getSelectedGroupsCount, selectedGroups })
                      ? 'grey'
                      : '#5E5047',
                    '&.Mui-checked': {
                      color: 'black',
                    },
                    padding: '0 !important',
                    margin: '0 !important'
                  }}
                  checked={allGroupsChecked}
                  onChange={handleSelectAllCheckBoxChange}
                />
              )}
              sx={{ padding: '0', margin: '0', paddingRight: '8px' }}
            />
            {isMobileView ? '' : t('group-modal-table-heading-select-all')}
          </TableCell>

          <TableCell sx={{ fontFamily: 'Johnson Display', fontSize: '14px', fontWeight: 'bold', borderRight: isMobileView ? 'unset' : '1px solid #C1BBB3', borderBottom: isMobileView ? 'unset' : '1px solid #C1BBB3', paddingLeft: '13px !important', width: isMobileView ?'45%' : '70%' }}>
            <TableSortLabel
              active={sortBy === 'displayName'}
              direction={sortOrder}
              onClick={() => handleSort('displayName')}
              style={{ flexDirection: 'row-reverse' }}
            >
              {t('group-modal-table-heading-group-name')}
            </TableSortLabel>
          </TableCell>
          {isMobileView ? '' :
            <TableCell sx={{ fontFamily: 'Johnson Display', fontSize: '14px', fontWeight: 'bold', paddingLeft: '16px !important', borderBottom: '1px solid #C1BBB3', width: '48%' }}>{t('group-modal-table-heading-description')}</TableCell>
          }
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedGroups.map((group) => (
          <TableRow key={group.id} sx={{
            verticalAlign: isMobileView ? 'baseline' : 'middle',
            '& .MuiTableCell-root': {
              height: '54px',
              padding: '0'
            },
          }}>
            <TableCell sx={{ borderRight: '1px solid #C1BBB3', borderBottom: '1px solid #C1BBB3', paddingLeft: '10px !important' }}>
              <FormControlLabel
                control={(
                  <Checkbox
                    disabled={shouldDisableGroup({ group, isSuperAdminUser, getSelectedGroupsCount, selectedGroups })}
                    checked={isGroupSelected(group.id, selectedGroups)}
                    onChange={() => handleCheckboxChange(group)}
                    sx={{
                      color: shouldDisableGroup({ group, isSuperAdminUser, getSelectedGroupsCount, selectedGroups })
                        ? 'grey'
                        : '#5E5047',
                      zIndex: 1,
                      '&.Mui-checked': {
                        color: 'black',
                      },
                      padding: '0 !important',
                      margin: '0 !important'
                    }}
                  />
                )}
                sx={{ padding: '0', margin: '0', paddingRight: '8px' }}
              />
              {isMobileView ? '' : t('group-modal-table-row-select')}
            </TableCell>
            <TableCell sx={{ borderRight: isMobileView ? 'unset' : '1px solid #C1BBB3', borderBottom: '1px solid #C1BBB3', padding: isMobileView ? '14px 0 14px 13px !important' : '0 0 0 13px !important' }}>
              <Chip
                label={group.displayName}
                variant="outlined"
                sx={{
                  fontSize: '16px !important',
                  font: 'Johnson Text !important'
                }}
              />
              {isMobileView ? <div style={{marginTop: '20px'}}>
                <Typography style={{ fontSize: '14px', fontFamily: 'Johnson Display', fontWeight: 'bold' }}>{t('group-modal-table-heading-description')}</Typography>
                <Typography style={{ fontSize: '14px', marginTop: '0px', fontFamily: 'Johnson Display' }}>{group.description}</Typography>
              </div> : ''}
            </TableCell>
            {isMobileView ? '' :
              <TableCell sx={{ fontFamily: 'Johnson Text', paddingLeft: '16px !important', paddingTop: '12px !important', paddingBottom: '8px !important', borderBottom: '1px solid #C1BBB3' }}>
                {group.description}
              </TableCell>
            }
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
  if (areGroupsLoading) {
    return (
      <div style={{
        display: 'flex',
        height: '15vw',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <LoadingSpinner centered={false} />
      </div>
    )
  }
  return (
    <TableContainer sx={{ height: '100%', width: '100%', maxHeight: '326px', overflowY: 'scroll' }}>{renderTable()}</TableContainer>
  );
};

export default GroupsTable;
